/**
 * Page-local URL params for IrsNoticesPage's four list-affecting pieces of state that were NOT
 * surviving back-navigation or a refresh: status, assignee, taxYear and openOnly -- all four feed
 * the `filters` object passed into the `irsNotices` query key. Seventh adoption of the pattern
 * #78 established (after #97-#101).
 *
 * The detail panel (`selectedNoticeId`, `selectedClientId`, `detailPanelOpen`) is deliberately
 * OUT of scope: reset on close, tied to one notice, not view configuration.
 *
 * `openOnly` is the one boolean in this sweep that defaults to TRUE (a work queue, not an
 * archive, per the page's own comment) -- the opposite stable-default shape from every other
 * boolean here. So the URL param is `open=0` meaning "show closed too", omitted when true.
 *
 * `clearAll` mirrors the page's existing `clearFilters()` handler, which resets all four at once
 * from a single button click -- all four writes land in the same `setSearchParams` call so they
 * commit together, not as four separate history entries.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { IrsNoticeStatus } from '../types';

const VALID_STATUSES: readonly IrsNoticeStatus[] = [
  'received', 'under_review', 'awaiting_client_info', 'amendment_in_progress',
  'awaiting_irs_response', 'resolved', 'closed_no_action',
];

export interface IrsNoticesScopeParams {
  status: IrsNoticeStatus | undefined;
  assignee: string | undefined;
  taxYear: number | undefined;
  openOnly: boolean;
  setStatus: (status: IrsNoticeStatus | undefined) => void;
  setAssignee: (assignee: string | undefined) => void;
  setTaxYear: (taxYear: number | undefined) => void;
  setOpenOnly: (openOnly: boolean) => void;
  clearAll: () => void;
}

export function useIrsNoticesScopeParams(): IrsNoticesScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const status = useMemo<IrsNoticeStatus | undefined>(() => {
    const raw = searchParams.get('status');
    return raw && (VALID_STATUSES as string[]).includes(raw) ? (raw as IrsNoticeStatus) : undefined;
  }, [searchParams]);

  const assignee = searchParams.get('assignee') || undefined;

  const taxYear = useMemo(() => {
    const raw = searchParams.get('taxYear');
    if (!raw) return undefined;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : undefined;
  }, [searchParams]);

  const openOnly = searchParams.get('open') !== '0';

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setStatus = useCallback(
    (next: IrsNoticeStatus | undefined) => {
      update((params) => {
        if (next === undefined) params.delete('status');
        else params.set('status', next);
      });
    },
    [update]
  );

  const setAssignee = useCallback(
    (next: string | undefined) => {
      update((params) => {
        if (!next) params.delete('assignee');
        else params.set('assignee', next);
      });
    },
    [update]
  );

  const setTaxYear = useCallback(
    (next: number | undefined) => {
      update((params) => {
        if (next === undefined) params.delete('taxYear');
        else params.set('taxYear', String(next));
      });
    },
    [update]
  );

  const setOpenOnly = useCallback(
    (next: boolean) => {
      update((params) => {
        if (next) params.delete('open');
        else params.set('open', '0');
      });
    },
    [update]
  );

  const clearAll = useCallback(() => {
    update((params) => {
      params.delete('status');
      params.delete('assignee');
      params.delete('taxYear');
      params.delete('open');
    });
  }, [update]);

  return { status, assignee, taxYear, openOnly, setStatus, setAssignee, setTaxYear, setOpenOnly, clearAll };
}
